import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import ownershipAccountService from '../../../../services/ownershipAccountService';
import venueSupplierService from '../../../../services/venueSupplierService';
import type { OwnershipAccountType, Supplier } from '../../../../types/venue';

const TYPE_LABELS: Record<OwnershipAccountType, string> = {
  VENUE_OWNED:     'Venue Owned',
  CONSIGNED:       'Consigned',
  EXTERNAL_ENTITY: 'External Entity',
};

const TYPES: OwnershipAccountType[] = ['VENUE_OWNED', 'CONSIGNED', 'EXTERNAL_ENTITY'];

interface FormState {
  name: string;
  type: OwnershipAccountType;
  supplierId: string;
  active: boolean;
  notes: string;
}

const OwnershipAccountForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { venueId } = useVenue();
  const { accountId } = useParams<{ accountId: string }>();
  const isEdit = !!accountId;

  const defaultType = (searchParams.get('type') as OwnershipAccountType | null) ?? 'CONSIGNED';
  const defaultSupplierId = searchParams.get('supplierId') ?? '';

  const [form, setForm] = useState<FormState>({
    name: '',
    type: defaultType,
    supplierId: defaultSupplierId,
    active: true,
    notes: '',
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const backPath = `/admin/venues/${venueId}/ownership-accounts`;

  useEffect(() => {
    if (!venueId) return;
    venueSupplierService.listVenueSuppliers(venueId, 1, 500).then(res => setSuppliers(res.data)).catch(() => {});
  }, [venueId]);

  useEffect(() => {
    if (!isEdit || !venueId || !accountId) return;
    ownershipAccountService.getAccount(venueId, accountId)
      .then(acc => {
        setForm({
          name: acc.name,
          type: acc.type,
          supplierId: acc.supplierId ?? '',
          active: acc.active,
          notes: acc.notes ?? '',
        });
      })
      .catch(() => setError('Failed to load account.'))
      .finally(() => setLoading(false));
  }, [isEdit, venueId, accountId]);

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!venueId) return;
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        supplierId: form.type === 'CONSIGNED' && form.supplierId ? form.supplierId : null,
        active: form.active,
        ...(form.notes.trim() && { notes: form.notes.trim() }),
      };
      if (isEdit && accountId) {
        await ownershipAccountService.updateAccount(venueId, accountId, payload);
      } else {
        await ownershipAccountService.createAccount(venueId, payload);
      }
      navigate(backPath);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'DUPLICATE_NAME') {
        setError('An account with this name already exists for this venue.');
      } else if (code === 'CANNOT_DEACTIVATE_DEFAULT') {
        setError('Cannot deactivate the venue default account. Assign a different default first.');
      } else {
        setError(err?.response?.data?.message ?? 'Failed to save account.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 600 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(backPath)}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Ownership Accounts
        </Button>
        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        <Typography variant="h6" fontWeight={700}>
          {isEdit ? 'Edit Account' : 'New Ownership Account'}
        </Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Name *"
            value={form.name}
            onChange={e => handleChange('name', e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            select
            label="Type *"
            value={form.type}
            onChange={e => handleChange('type', e.target.value as OwnershipAccountType)}
            size="small"
            fullWidth
            disabled={isEdit}
            helperText={isEdit ? 'Type cannot be changed after creation.' : undefined}
          >
            {TYPES.map(t => (
              <MenuItem key={t} value={t}>{TYPE_LABELS[t]}</MenuItem>
            ))}
          </TextField>

          {!isEdit && form.type === 'VENUE_OWNED' && (
            <Alert severity="info" sx={{ py: 0.75 }}>
              Each venue already has one Venue Stock default account. Create this type only if you need a second distinct venue-owned pool.
            </Alert>
          )}

          {form.type === 'CONSIGNED' && (
            <TextField
              select
              label="Linked Supplier"
              value={form.supplierId}
              onChange={e => handleChange('supplierId', e.target.value)}
              size="small"
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {suppliers.map(s => (
                <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            label="Notes"
            value={form.notes}
            onChange={e => handleChange('notes', e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
            placeholder="Optional"
          />

          {isEdit && (
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                pt: 1,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={form.active}
                    onChange={e => handleChange('active', e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Active</Typography>}
              />
            </Box>
          )}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button
          onClick={() => navigate(backPath)}
          sx={{ textTransform: 'none' }}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {isEdit ? 'Save Changes' : 'Create Account'}
        </Button>
      </Box>
    </Box>
  );
};

export default OwnershipAccountForm;
